import { PageTemplate, HeaderTemplate, ButtonLink, Button } from "../styles";
import { useLocation } from "react-router-dom";
import Form from "react-bootstrap/Form";
import { useState, useEffect, useContext } from "react";
import { createReturn } from "../api/order";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { DataContext } from "../DataContext";
import { useAuth0 } from "@auth0/auth0-react";
import { ReturnReviewTemplate } from "../templates/ReturnReviewTemplate";
import toast from "react-hot-toast";
import styled from "styled-components";
import { SIZE_KEY_MAP } from "../constants";

const ReviewButton = styled(Button)`
  width: fit-content;
  margin: 0 auto;
  padding: 8px 40px;
`;

const OrderNumber = styled.p`
  background-color: var(--dark-grey-highlight);
  color: var(--white);
  border-radius: 5px;
  padding: 20px 30px;
  margin: 0 0 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  & > div {
    font-weight: 700;
  }
  @media only screen and (max-width: 991px) {
    padding: 20px;
    flex-direction: column;
    text-align: center;
    & > div:last-child {
      margin-top: 10px;
    }
  }
`;

const Return = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  const { getCustomer, catalogProducts } = useContext(DataContext);
  const { isAuthenticated } = useAuth0();

  const currentOrder = state?.order;

  const [products, setProducts] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [shippingRate, setShippingRate] = useState(null);
  const [productsWithErrors, setProductsWithErrors] = useState([]);
  const [formSubmittedWithDropdownErrors, setFormSubmittedWithDropdownErrors] = useState(false);

  // component mount
  useEffect(() => {
    const fetchData = async () => {
      if (currentOrder) {
        if (currentOrder.returns.length > 0) {
          setProducts(filterReturnedProducts(currentOrder.returns[currentOrder.returns.length - 1].products));
        } else {
          setProducts(flattenProducts(currentOrder.products));
        }
        setShippingRate(currentOrder.shippingRate);
      }
    };
    fetchData();
  }, []);

  // products update
  useEffect(() => {
    if (formSubmittedWithDropdownErrors) {
      createItemErrorList();
    }
  }, [products]);

  const filterReturnedProducts = (products) => {
    const filteredProducts = products.filter((product) => {
      return product.returnType === "Keep";
    });
    return filteredProducts;
  };

  const flattenProducts = (productsArray) => {
    const flattenedProducts = [];

    productsArray.forEach((product) => {
      for (let i = 0; i < product.quantity; i++) {
        flattenedProducts.push({
          returnType: "Keep",
          returnReasons: [],
          sizeExchange: null,
          selected: false,
          design: product.design,
          itemId: Math.floor(Math.random() * 1000000000),
          productName: product.productName,
          productCost: product.productCost,
          size: product.size,
          productId: product.productId,
          lookupKey: product.lookupKey,
          thumbnail: product.thumbnail,
        });
      }
    });

    return flattenedProducts;
  };

  const getStyleInventory = (product) => {
    const catalogProduct = catalogProducts.find((item) => item.productId === product.productId);

    if (!catalogProduct) return null;

    const style = catalogProduct.styles.find((item) => item.lookupKey === product.lookupKey);

    return style?.inventory || null;
  };

  const getAvailableExchangeSizes = (product) => {
    const inventory = getStyleInventory(product);
    if (!inventory) return [];

    const currentSizeKey = SIZE_KEY_MAP[product.size];

    return Object.entries(inventory)
      .filter(([sizeKey, qty]) => {
        return Number(qty) > 0 && sizeKey !== currentSizeKey;
      })
      .map(([sizeKey]) => ({
        value: sizeKey.toUpperCase(),
        label: sizeKey.toUpperCase(),
      }));
  };

  // this creates a list of products with errors to display an error background color
  const createItemErrorList = () => {
    const errors = [];
    products.forEach(({ returnType, returnReasons, sizeExchange, itemId, selected }) => {
      if (
        (selected && returnType === "Keep") ||
        (selected && (!returnReasons || returnReasons.length === 0)) ||
        (returnType === "Exchange" && (sizeExchange === null || sizeExchange === ""))
      ) {
        errors.push(itemId);
      }
    });
    setProductsWithErrors(errors);
  };

  const handleReviewReturn = () => {
    let errorMessage = null;

    const selectedProducts = products.filter((product) => {
      return product.selected === true;
    });

    createItemErrorList();

    const allReturnTypesSelected = selectedProducts.every((product) => {
      return product.returnType !== "Keep" && product.returnReasons && product.returnReasons.length > 0;
    });

    const exchangeDropdownsSelected = selectedProducts.every(({ returnType, sizeExchange }) => {
      if (returnType === "Exchange") {
        return sizeExchange !== null && sizeExchange !== "";
      } else {
        return true;
      }
    });

    if (selectedProducts.length === 0) {
      // make sure at least one product has been selected
      errorMessage = "Choose at least one product";
    } else if (selectedProducts.length > 0 && !allReturnTypesSelected) {
      // make sure all selected products have returnType Refund or Exchange
      // and all have a return reason
      errorMessage = "Choose a return type and reason for all selected products";
      setFormSubmittedWithDropdownErrors(true);
    } else if (selectedProducts.length > 0 && !exchangeDropdownsSelected) {
      // if product returnType is Exchange, make sure sizeExchange has values
      errorMessage = "Choose a new size for all selected products to be exchanged.";
      setFormSubmittedWithDropdownErrors(true);
    }

    if (!errorMessage) {
      setShowReviewModal(true);
    } else {
      toast.error(errorMessage);
    }
  };

  const handleReturnSubmission = async () => {
    try {
      const returnObj = {
        order: currentOrder,
        products,
        orderProductCount: flattenProducts(currentOrder.products).length,
      };
      await createReturn(returnObj);
      // only get customer if logged in
      if (isAuthenticated) {
        getCustomer();
      }
      navigate("/return/confirmation", {
        state: {
          orderNumber: currentOrder.orderNumber,
          orderEmail: currentOrder.billing.email,
        },
      });
    } catch (error) {
      console.error(error);
      toast.error(error.message);
    }
  };

  const renderProducts = () => {
    return products?.map((product) => {
      const hasError = !!productsWithErrors.find((item) => {
        return item === product.itemId;
      });
      return (
        <div className="start-return-exchange-row" key={product._id}>
          <div className="return-exchange-product">
            <div className="return-exchange-checkbox">
              <Form.Check
                onChange={(e) => handleProductSelect(product.itemId, e.target.checked)}
                type={"checkbox"}
                id={"separate-billing"}
                label={""}
                checked={product.selected}
              />
            </div>
            <div className="product-row">
              <div className="product-image">
                <picture>
                  <source srcSet={product.thumbnail} type="image/avif" />
                  <img src={product.thumbnail} width="140" />
                </picture>
              </div>
              <div className="product-block">
                <h2>{product.productName}</h2>
                <div className="product-block-row">
                  <span className="product-block-label">Color</span>
                  <strong className="product-block-value">{product.design}</strong>
                </div>
                <div className="product-block-row">
                  <span className="product-block-label">Size</span>
                  <strong className="product-block-value">{product.size}</strong>
                </div>
                <div className="product-block-row">
                  <span className="product-block-label">Price</span>
                  <strong className="product-block-value">${(product.productCost / 100).toFixed(2)}</strong>
                </div>
              </div>
            </div>
          </div>
          <div className={`return-exchange-dropdowns ${!product.selected && "hide"} ${hasError && "error"}`}>
            <div className="left-return-options">
              <div className="return-type">
                <Form.Select
                  name="returnType"
                  id="returnType"
                  value={product.returnType}
                  onChange={(e) => handleSelection(product.itemId, "returnType", e.target.value)}
                >
                  <option value="Keep">Select Return Type</option>
                  {currentOrder.returns.length === 0 && <option value="Exchange">Exchange</option>}
                  <option value="Refund">Refund</option>
                </Form.Select>
              </div>
              <div className="return-reason">
                <div className="return-reasons">
                  <Form.Check
                    type="checkbox"
                    label="Too Short"
                    checked={product.returnReasons?.includes("too-short")}
                    onChange={(e) =>
                      handleReturnReasonChange(product.itemId, "too-short", e.target.checked)
                    }
                  />

                  <Form.Check
                    type="checkbox"
                    label="Too Long"
                    checked={product.returnReasons?.includes("too-long")}
                    onChange={(e) =>
                      handleReturnReasonChange(product.itemId, "too-long", e.target.checked)
                    }
                  />

                  <Form.Check
                    type="checkbox"
                    label="Too Tight"
                    checked={product.returnReasons?.includes("too-tight")}
                    onChange={(e) =>
                      handleReturnReasonChange(product.itemId, "too-tight", e.target.checked)
                    }
                  />

                  <Form.Check
                    type="checkbox"
                    label="Too Loose"
                    checked={product.returnReasons?.includes("too-loose")}
                    onChange={(e) =>
                      handleReturnReasonChange(product.itemId, "too-loose", e.target.checked)
                    }
                  />
                </div>
              </div>
            </div>

            <div className="right-return-options">
              {product.returnType === "Exchange" && (
                <div className="exchange-dropdowns">
                  <Form.Select
                    name="sizeExchange"
                    id="sizeExchange"
                    value={product.sizeExchange}
                    onChange={(e) => handleSelection(product.itemId, "sizeExchange", e.target.value)}
                  >
                    <option value="">Exchange Size</option>
                    {getAvailableExchangeSizes(product).map((size) => (
                      <option key={size.value} value={size.value}>
                        {size.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              )}
              {product.returnType === "Refund" && (
                <div className="refund-amount">
                  Refund Amount<span>${(product.productCost / 100).toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    });
  };

  // removes error from updated product
  const updateFormErrors = (itemId) => {
    setFormSubmittedWithDropdownErrors(false);
    const updatedProducts = productsWithErrors.filter((item) => {
      return item !== itemId;
    });
    setProductsWithErrors(updatedProducts);
  };

  const handleProductSelect = (itemId, value) => {
    updateFormErrors(itemId);
    const item = products.find((product) => {
      return product.itemId === itemId;
    });
    let updatedItem = { ...item, selected: value };

    const updatedProductArray = products.map((product) => {
      if (itemId === product.itemId) {
        if (value === true) {
          return updatedItem;
        } else {
          return {
            ...updatedItem,
            sizeExchange: null,
            returnType: "Keep",
            returnReasons: [],
          };
        }
      } else {
        return product;
      }
    });
    setProducts(updatedProductArray);
  };


  const handleReturnReasonChange = (itemId, reason, isChecked) => {
    updateFormErrors(itemId);

    const oppositeReasons = {
      "too-short": "too-long",
      "too-long": "too-short",
      "too-tight": "too-loose",
      "too-loose": "too-tight",
    };

    const updatedProductArray = products.map((product) => {
      if (product.itemId !== itemId) {
        return product;
      }

      const currentReasons = product.returnReasons || [];

      let updatedReasons;

      if (isChecked) {
        updatedReasons = currentReasons.filter((item) => item !== oppositeReasons[reason]);

        if (!updatedReasons.includes(reason)) {
          updatedReasons.push(reason);
        }
      } else {
        updatedReasons = currentReasons.filter((item) => item !== reason);
      }

      return {
        ...product,
        returnReasons: updatedReasons,
      };
    });

    setProducts(updatedProductArray);
  };

  const handleSelection = (itemId, field, value) => {
    updateFormErrors(itemId);
    const item = products.find((product) => {
      return product.itemId === itemId;
    });
    let updatedItem = { ...item, [field]: value };
    if (updatedItem.returnType === "Keep" || updatedItem.returnType === "Refund") {
      updatedItem = {
        ...updatedItem,
        sizeExchange: null,
      };
    }
    const updatedProductArray = products.map((product) => {
      if (itemId === product.itemId) {
        return updatedItem;
      } else {
        return product;
      }
    });
    setProducts(updatedProductArray);
  };

  if (!state) return null;

  console.log("bobtest1", products);

  return (
    <>
      <HeaderTemplate className="narrow">
        <div className="header flex-header no-line">
          <div className="title">
            <h1>Start a Return</h1>
          </div>
          {isAuthenticated && (
            <div className="breadcrumb">
              <ButtonLink to="/account" className="small clear">
                Back to Orders
              </ButtonLink>
            </div>
          )}
        </div>
        <OrderNumber>
          <div>Select the product(s) that you would like to return.</div>
          <div>
            Order # <span>{currentOrder.orderNumber}</span>
          </div>
        </OrderNumber>
      </HeaderTemplate>
      <PageTemplate className="narrow">
        <div className="body">
          <div className="start-return">{renderProducts()}</div>

          <ReviewButton onClick={handleReviewReturn}>Continue</ReviewButton>
          <p style={{ marginTop: "20px", fontStyle: "italic", padding: "0 20px", textAlign: "center" }}>
            <strong>1 Free Return Per Order</strong>
            <br />
            One free return allowed per order. Additional returns are subject to a shipping fee.
          </p>
        </div>
      </PageTemplate>
      <Modal size={"lg"} fullscreen={"md-down"} show={showReviewModal} onHide={() => setShowReviewModal(false)}>
        <ReturnReviewTemplate
          products={products}
          submit={handleReturnSubmission}
          isFirstReturn={currentOrder.returns.length === 0}
          salesTax={currentOrder.salesTax}
          customerPostalCode={currentOrder.shipping.address.postal_code}
          subTotal={currentOrder.subTotal}
        />
      </Modal>
    </>
  );
};

export default Return;
